import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useGetGuestBookingDataQuery, useGetGuestQuoteQuery } from "@/redux/apis/guest/guestApi";
import { useAppSelector } from "@/redux/hooks";
import { buildGuestQuoteBody, getMissingBookingFields } from "@/redux/slices/clientBooking/bookingPayload";
import { selectClientBookingDraft } from "@/redux/slices/clientBooking/selectors";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";

/**
 * Guest twin of useBookingQuote. Live-prices the guest draft against POST /v2/guest-quote and
 * re-quotes automatically as the draft changes. The guest-quote endpoint needs the transfer/vehicle
 * UUIDs (it rejects numeric ids), so the guest job-data is fetched here purely to resolve them —
 * buildGuestQuoteBody returns null (and the query is skipped) until it can. Unlike the authed hook
 * there is no payment-method gate: the guest pays with a tokenised pm_ that is not part of quoting,
 * so "Payment method" is dropped from the missing-fields hint. Returns the full itemised `quote`
 * (Journey Cost / Extras / fees / Total) — the same shape the authed quote returns (see GuestQuote).
 */
export const useGuestBookingQuote = () => {
  const draft = useAppSelector(selectClientBookingDraft);
  const providerUuid = useAppSelector(operatorIdSelector);

  // Fetched only to map the draft's numeric transfer id / vehicle id → the uuids the quote endpoint needs.
  const { data: bookingData } = useGetGuestBookingDataQuery(providerUuid ?? skipToken);

  const body = useMemo(() => buildGuestQuoteBody(draft, providerUuid, bookingData), [draft, providerUuid, bookingData]);
  // Payment is captured as a tokenised pm_ (not part of quoting), so it never blocks the guest quote.
  const missingFields = useMemo(() => getMissingBookingFields(draft).filter((field) => field !== "Payment method"), [draft]);

  const { data: quote, isFetching: isLoading } = useGetGuestQuoteQuery(body ?? skipToken);

  return {
    /** Full itemised quote (Journey Cost / Extras / fees / Total, AUD dollars). Undefined until priced. */
    quote,
    /** True once the draft is complete enough to quote AND the transfer/vehicle uuids have resolved. */
    isQuotable: body !== null,
    isLoading,
    /** Human-readable labels of what still blocks the quote (payment excluded — see above). */
    missingFields,
  };
};
